import * as Dialog from '@radix-ui/react-dialog';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import React from 'react';

export type PermissionPromptModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string;
  primaryLabel: string;
  onPrimary: () => void;
  onSkip: () => void;
};

// ─── styled ───

const Overlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1099;
`;

const CenteredContent = styled(Dialog.Content)`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const Container = styled.div`
  width: 328px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: #26282d;
  border: 1px solid #2b2d33;
  border-radius: 16px;
  padding: 24px 20px;
`;

const Title = styled.h3`
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 17px;
  line-height: 120%;
  color: #e9ecf5;
`;

const Subtitle = styled.p`
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  color: #8c92a3;
`;

const ButtonsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const PrimaryButton = styled.button`
  width: 288px;
  height: 45px;
  padding: 12px 16px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  background: #5e84ed;

  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 15px;
  line-height: 140%;
  text-align: center;
  color: #ffffff;
`;

const SkipButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px 16px;

  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 15px;
  line-height: 140%;
  text-align: center;
  color: #b7bdcb;
`;

// ─── component ───

const PermissionPromptModal: React.FC<PermissionPromptModalProps> = ({
  open,
  onOpenChange,
  title,
  subtitle,
  primaryLabel,
  onPrimary,
  onSkip,
}) => {
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return;
    onSkip();
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Overlay onClick={handleOverlayClick} />
        <CenteredContent
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onClick={handleOverlayClick}
          aria-describedby={undefined}
        >
          <Dialog.Title style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
            {title}
          </Dialog.Title>
          <motion.div
            initial={{ scale: 0.98, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.98, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.22, 0.61, 0.36, 1] }}
          >
            <Container>
              <Title>{title}</Title>
              {subtitle && <Subtitle>{subtitle}</Subtitle>}

              <ButtonsCol>
                <PrimaryButton onClick={onPrimary}>
                  {primaryLabel}
                </PrimaryButton>
                <SkipButton onClick={onSkip}>Skip</SkipButton>
              </ButtonsCol>
            </Container>
          </motion.div>
        </CenteredContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PermissionPromptModal;
